import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  StateWorkType,
  WorkElement,
  WorkElementDetailsPayload,
  WorkTreeNode,
  WorkSearchScope,
  WorkThread,
} from './work.models';
import { workFeatureKey } from './work.reducer';
import { KanbanStatus, resolveKanbanStatus } from '@shared/models/kanban-status.enum';
import { IEBWorkTreeNode } from '@shared/models/entities/work-tree-node.interface';

export const selectWorkState =
  createFeatureSelector<StateWorkType>(workFeatureKey);

export const selectEntities = createSelector(
  selectWorkState,
  (state) => state.entities,
);

export const selectUi = createSelector(selectWorkState, (state) => state.ui);

export const selectLoading = createSelector(
  selectWorkState,
  (state) => state.loading,
);

export const selectErrors = createSelector(
  selectWorkState,
  (state) => state.errors,
);

export const selectCurrentElementId = createSelector(
  selectUi,
  (ui) => ui.selectedElementId,
);

export const selectSelectedChildId = createSelector(
  selectUi,
  (ui) => ui.selectedChildId,
);

export const selectExpandedNodeIds = createSelector(
  selectUi,
  (ui) => ui.expandedNodeIds,
);

export const selectQuickFilter = createSelector(
  selectUi,
  (ui) => ui.quickFilter,
);

export const selectTagFilter = createSelector(
  selectUi,
  (ui) => ui.tagFilter,
);

export const selectSearchTerm = createSelector(selectUi, (ui) => ui.searchTerm);

export const selectSearchScope = createSelector(
  selectUi,
  (ui) => ui.searchScope,
);

export const selectActiveTabKey = createSelector(
  selectUi,
  (ui) => ui.activeTabKey,
);

export const selectPartyTree = createSelector(selectUi, (ui) => ui.partyTree);

/** Selector for the full party tree (always from party root) - used for navigation */
export const selectFullPartyTree = createSelector(selectUi, (ui) => ui.fullPartyTree);

export const selectPreview = createSelector(selectUi, (ui) => ui.preview);

/**
 * Count statuses from the party tree (before any filtering).
 * Returns an array of { status, count } for each KanbanStatus.
 */
export const selectPartyTreeStatusCounts = createSelector(
  selectPartyTree,
  (tree): { status: KanbanStatus; count: number }[] => {
    const counts = new Map<KanbanStatus, number>();

    // Initialize all statuses with 0
    Object.values(KanbanStatus).forEach((s) => counts.set(s as KanbanStatus, 0));

    // Recursively count statuses in tree (excluding root party node)
    const countNode = (node: IEBWorkTreeNode) => {
      if (node.status) {
        const status = resolveKanbanStatus(node.status);
        counts.set(status, (counts.get(status) ?? 0) + 1);
      }
      node.children?.forEach(countNode);
    };

    tree?.children?.forEach(countNode);

    return Array.from(counts.entries()).map(([status, count]) => ({
      status,
      count,
    }));
  }
);

// Dumb search: filter party tree by plain substring on node name
export const selectSearchFilteredPartyTree = createSelector(
  selectPartyTree,
  selectSearchTerm,
  (tree, term) => {
    const normalized = (term ?? '').toLowerCase().trim();
    if (!tree || !normalized) return tree;

    const prune = (node: IEBWorkTreeNode): IEBWorkTreeNode | null => {
      const name = (node?.name ?? '').toLowerCase();
      const matched = name.includes(normalized);
      const prunedChildren = (node?.children ?? [])
        .map((c) => prune(c))
        .filter((c): c is IEBWorkTreeNode => !!c);
      if (matched || prunedChildren.length > 0) {
        return { ...node, children: prunedChildren };
      }
      return null;
    };

    const prunedChildren = (tree.children ?? [])
      .map((c) => prune(c))
      .filter((c): c is IEBWorkTreeNode => !!c);
    return { ...tree, children: prunedChildren } as typeof tree;
  },
);

/**
 * Filter party tree by status (quick filter).
 * Keeps nodes that match any selected status, plus parents with matching descendants.
 */
export const selectStatusFilteredPartyTree = createSelector(
  selectSearchFilteredPartyTree,
  selectQuickFilter,
  (tree, selectedStatuses) => {
    // If no statuses selected or all statuses selected, return tree as-is
    if (!tree || selectedStatuses.length === 0) return tree;

    const allStatuses = Object.values(KanbanStatus);
    if (selectedStatuses.length === allStatuses.length) return tree;

    const pruneByStatus = (node: IEBWorkTreeNode): IEBWorkTreeNode | null => {
      // Check if node matches any selected status
      const nodeStatus = node.status ? resolveKanbanStatus(node.status) : null;
      // Nodes without status (e.g., parties) are always kept if they have matching descendants
      const nodeMatches = !nodeStatus || selectedStatuses.includes(nodeStatus);

      // Recursively prune children
      const prunedChildren = (node.children ?? [])
        .map((c) => pruneByStatus(c))
        .filter((c): c is IEBWorkTreeNode => c !== null);

      // Keep node if it matches OR has matching descendants
      if (nodeMatches || prunedChildren.length > 0) {
        return { ...node, children: prunedChildren };
      }
      return null;
    };

    const prunedChildren = (tree.children ?? [])
      .map((c) => pruneByStatus(c))
      .filter((c): c is IEBWorkTreeNode => c !== null);

    return { ...tree, children: prunedChildren } as typeof tree;
  }
);

/**
 * Filter party tree by tags.
 * Keeps nodes that have any of the selected tags, plus parents with matching descendants.
 */
export const selectTagFilteredPartyTree = createSelector(
  selectStatusFilteredPartyTree,
  selectTagFilter,
  (tree, selectedTagIds) => {
    // If no tags selected, return tree as-is
    if (!tree || selectedTagIds.length === 0) return tree;

    const pruneByTags = (node: IEBWorkTreeNode): IEBWorkTreeNode | null => {
      // Check if node has any of the selected tags
      const nodeTags = node.tags ?? [];
      const nodeMatches = nodeTags.some((tag) => selectedTagIds.includes(tag.tagId));

      // Recursively prune children
      const prunedChildren = (node.children ?? [])
        .map((c) => pruneByTags(c))
        .filter((c): c is IEBWorkTreeNode => c !== null);

      // Keep node if it matches OR has matching descendants
      // Also keep nodes without tags (parties, campaigns) if they have matching descendants
      if (nodeMatches || prunedChildren.length > 0) {
        return { ...node, children: prunedChildren };
      }
      return null;
    };

    const prunedChildren = (tree.children ?? [])
      .map((c) => pruneByTags(c))
      .filter((c): c is IEBWorkTreeNode => c !== null);

    return { ...tree, children: prunedChildren } as typeof tree;
  }
);

/**
 * Combined filtered party tree: search + status + tag filtering.
 * This is the selector to use in the work component.
 */
export const selectFilteredPartyTree = selectTagFilteredPartyTree;

export const selectPreviewLoading = createSelector(
  selectLoading,
  selectSelectedChildId,
  (loading, selectedChildId) =>
    selectedChildId ? loading.elementLoadingById[selectedChildId] === true : false,
);

export const selectElementById = (id: string | null) =>
  createSelector(selectEntities, (entities) => (id ? entities.elementsById[id] ?? null : null));

export const selectChildrenIdsByParent = (parentId: string | null) =>
  createSelector(selectEntities, (entities) =>
    parentId ? entities.childrenByParentId[parentId] ?? [] : [],
  );

export const selectCurrentElement = createSelector(
  selectEntities,
  selectCurrentElementId,
  (entities, id) => (id ? entities.elementsById[id] ?? null : null),
);

/**
 * Helper: Find the parent of an element in a tree.
 * Returns:
 * - The parent node if found
 * - null if the element is the root (no parent)
 * - undefined if the element is not found in the tree
 */
function findParentInTree(
  elementId: string,
  tree: IEBWorkTreeNode
): IEBWorkTreeNode | null | undefined {
  const search = (
    node: IEBWorkTreeNode,
    parent: IEBWorkTreeNode | null
  ): IEBWorkTreeNode | null | undefined => {
    if (node.id === elementId) {
      return parent;
    }
    for (const child of node.children ?? []) {
      const found = search(child, node);
      if (found !== undefined) {
        return found;
      }
    }
    return undefined;
  };
  return search(tree, null);
}

/**
 * Determines if the current element can navigate up to a parent.
 * 
 * Strategy (generic, works at any level):
 * 1. If we have a fullPartyTree, search for the element and check if it has a parent there
 * 2. If not found in tree OR tree not loaded, fall back to checking element.parentId
 * 3. Only return false if we're certain there's no parent (at party root)
 * 
 * This ensures navigation works even when entering at lower levels before the full tree loads.
 */
export const selectCanNavigateUp = createSelector(
  selectCurrentElement,
  selectFullPartyTree,
  (currentElement, fullTree): boolean => {
    if (!currentElement) {
      return false;
    }

    // If element is a party (root type), check tree to see if it has a parent party
    // (usually parties are top-level, but we check the tree to be sure)
    if (currentElement.type === 'party') {
      if (!fullTree) {
        return false; // Party with no tree loaded - assume it's root
      }
      // If this party IS the root of the tree, can't go up
      if (fullTree.id === currentElement.id) {
        return false;
      }
      // Otherwise, this party is nested - can go up
      return true;
    }

    // For non-party elements (campaign, action, task):
    // Strategy 1: Check the fullPartyTree if available
    if (fullTree) {
      const parentInTree = findParentInTree(currentElement.id, fullTree);
      if (parentInTree !== undefined) {
        // Found the element in tree - return whether it has a parent
        return parentInTree !== null;
      }
      // Element not found in tree (tree might be stale or still loading)
      // Fall through to check parentId
    }

    // Strategy 2: Fall back to element's own parentId
    // Non-party elements should always have a parent
    return !!currentElement.parentId;
  }
);

export const selectCurrentChildren = createSelector(
  selectEntities,
  selectCurrentElementId,
  (entities, parentId) => buildTreeChildren(parentId, entities, { statuses: [], term: '', scope: 'tree' }),
);

export const selectSelectedChild = createSelector(
  selectEntities,
  selectSelectedChildId,
  (entities, selectedChildId) =>
    selectedChildId ? entities.elementsById[selectedChildId] ?? null : null,
);

export const selectCurrentDetails = createSelector(
  selectEntities,
  selectCurrentElementId,
  (entities, elementId): WorkElementDetailsPayload | null =>
    elementId ? entities.detailsByElementId[elementId] ?? null : null,
);

export const selectCurrentThreads = createSelector(
  selectEntities,
  selectCurrentElementId,
  (entities, elementId): WorkThread[] => (elementId ? entities.threadsByElementId[elementId] ?? [] : []),
);

export const selectIsLeaf = createSelector(
  selectCurrentElement,
  selectCurrentChildren,
  (element, children) => {
    const hasKnownChildren = (children?.length ?? 0) > 0;
    const markedHasChildren = element?.hasChildren === true;
    return !hasKnownChildren && !markedHasChildren;
  },
);

export const selectTreeLoading = createSelector(
  selectLoading,
  selectCurrentElementId,
  (loading, elementId) =>
    elementId ? loading.childrenLoadingByParent[elementId] === true : false,
);

export const selectElementLoading = createSelector(
  selectLoading,
  selectCurrentElementId,
  (loading, elementId) =>
    elementId ? loading.elementLoadingById[elementId] === true : false,
);

export const selectDetailsLoading = createSelector(
  selectLoading,
  selectCurrentElementId,
  (loading, elementId) =>
    elementId ? loading.detailsLoadingByElement[elementId] === true : false,
);

export const selectThreadsError = createSelector(
  selectErrors,
  selectCurrentElementId,
  (errors, elementId) => (elementId ? errors.threadsErrorByElement[elementId] ?? null : null),
);

export const selectThreadsLoading = createSelector(
  selectLoading,
  selectCurrentElementId,
  (loading, elementId) => (elementId ? loading.threadsLoadingByElement[elementId] === true : false),
);

export const selectFilteredChildren = createSelector(
  selectEntities,
  selectCurrentElementId,
  selectQuickFilter,
  selectSearchTerm,
  selectSearchScope,
  (entities, parentId, statuses, term, scope) => {
    if (scope !== 'tree') {
      return buildTreeChildren(parentId, entities, { statuses: [], term: '', scope });
    }
    return buildTreeChildren(parentId, entities, { statuses, term, scope });
  },
);

export const selectTreeViewModel = createSelector(
  selectFilteredChildren,
  selectSelectedChildId,
  selectExpandedNodeIds,
  (nodes, selectedChildId, expandedNodeIds) => ({
    nodes,
    selectedKey: selectedChildId,
    expandedKeys: expandedNodeIds,
  }),
);

export const selectWorkViewModel = createSelector(
  selectCurrentElement,
  selectFilteredChildren,
  selectSelectedChildId,
  selectSelectedChild,
  selectPreview,
  selectQuickFilter,
  selectSearchTerm,
  selectSearchScope,
  selectActiveTabKey,
  selectCurrentDetails,
  selectCurrentThreads,
  selectThreadsError,
  selectIsLeaf,
  selectElementLoading,
  selectTreeLoading,
  selectPreviewLoading,
  selectDetailsLoading,
  selectThreadsLoading,
  (
    currentElement,
    children,
    selectedChildId,
    selectedChild,
    preview,
    quickFilter,
    searchTerm,
    searchScope,
    activeTabKey,
    details,
    threads,
    threadsError,
    isLeaf,
    elementLoading,
    treeLoading,
    previewLoading,
    detailsLoading,
    threadsLoading,
  ) => ({
    currentElement,
    children,
    selectedChildId,
    selectedChild,
    preview,
    quickFilter,
    searchTerm,
    searchScope,
    activeTabKey,
    details,
    threads,
    threadsError,
    isLeaf,
    loading: {
      element: elementLoading,
      children: treeLoading,
      preview: previewLoading,
      details: detailsLoading,
      threads: threadsLoading,
    },
  }),
);

function buildTreeChildren(
  parentId: string | null,
  entities: StateWorkType['entities'],
  filter: { statuses: KanbanStatus[]; term: string; scope: WorkSearchScope },
): WorkTreeNode[] {
  const childIds = parentId ? entities.childrenByParentId[parentId] ?? [] : [];
  const normalizedTerm = (filter.term ?? '').toLowerCase().trim();

  const children = childIds
    .map((id) => entities.elementsById[id])
    .filter((child): child is WorkElement => !!child)
    .map((child) => {
      const hasKnownChildren = (entities.childrenByParentId[child.id]?.length ?? 0) > 0;
      const hasChildren = hasKnownChildren || child.hasChildren === true;
      return {
        id: child.id,
        parentId: child.parentId ?? null,
        title: child.title,
        status: child.status ?? null,
        type: child.type,
        isLeaf: !hasChildren,
        hasChildren,
        meta: child.meta ?? null,
        children: buildTreeChildren(child.id, entities, filter),
      } satisfies WorkTreeNode;
    });

  // Add "no children yet" placeholder for non-task nodes that have no children
  if (parentId && children.length === 0) {
    const parent = entities.elementsById[parentId];
    if (parent && parent.type !== 'task' && parent.hasChildren) {
      children.push({
        id: `${parentId}-placeholder`,
        parentId,
        title: 'No children yet',
        status: null,
        type: 'placeholder',
        isLeaf: true,
        hasChildren: false,
        meta: null,
        children: [],
      } satisfies WorkTreeNode);
    }
  }

  if (filter.scope !== 'tree') {
    return children;
  }

  return children.filter((child) => {
    const matchesStatus = !filter.statuses?.length || filter.statuses.includes(resolveKanbanStatus(child.status ?? ''));

    const matchesTerm = !normalizedTerm || child.title.toLowerCase().includes(normalizedTerm);
    return matchesStatus && matchesTerm;
  });
}

export const selectStatusFilterLabel = (status: KanbanStatus) =>
  createSelector(selectQuickFilter, (filters) => filters.includes(status));
